import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, Phone, Mail, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Reveal } from '@/components/ui/Reveal';
import { useSEO } from '@/lib/seo';
import api from '@/lib/api';
import { parseApiError } from '@/lib/parseApiError';
import { toast } from 'sonner';

const MAIN_EMAIL = 'ambulancesbeaulon@live.fr';

const TRANSPORT_LABELS = { taxi: 'Taxi', vsl: 'VSL', ambulance: 'Ambulance' };

export default function Contact() {
  useSEO({
    title: "Contact | Groupe Ambulances Beaulon",
    description: "Contactez le Groupe Ambulances Beaulon : téléphone, email, formulaire. Transport sanitaire dans tout l'Allier.",
    keywords: "contact ambulance allier, ambulance beaulon contact"
  });

  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ nom: '', email: '', telephone: '', sujet: '', message: '', rgpd: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);

  // Pre-fill from URL params when coming from /estimation-transport
  useEffect(() => {
    const source = searchParams.get('source');
    if (source !== 'estimation_transport') return;

    const type = searchParams.get('type_transport');
    const commune = searchParams.get('commune_depart') || '';
    const destination = searchParams.get('destination') || '';
    const distance = searchParams.get('distance_km') || '';
    const date = searchParams.get('date') || '';
    const heure = searchParams.get('heure') || '';
    const ar = searchParams.get('aller_retour') === '1';
    const estimation = searchParams.get('estimation') || '';
    const isCallback = searchParams.get('callback') === '1';

    const typeLabel = TRANSPORT_LABELS[type] || 'Transport';
    const sujet = isCallback
      ? `Demande de rappel — Estimation ${typeLabel}`
      : `Demande d'informations — Estimation ${typeLabel}`;

    const lines = [
      `Bonjour,`,
      ``,
      isCallback
        ? `Je souhaite être rappelé(e) suite à ma simulation en ligne :`
        : `Je souhaite obtenir des informations suite à ma simulation en ligne :`,
      ``,
      `• Type de transport : ${typeLabel}`,
      commune ? `• Départ : ${commune}` : null,
      destination ? `• Destination : ${destination}` : null,
      distance ? `• Distance estimée : ${distance} km` : null,
      date ? `• Date souhaitée : ${date}` : null,
      heure ? `• Heure souhaitée : ${heure}` : null,
      `• Aller-retour : ${ar ? 'Oui' : 'Non'}`,
      estimation ? `• Estimation obtenue : environ ${estimation} €` : null,
      ``,
      `Merci de me recontacter pour organiser ce transport.`,
    ].filter(Boolean).join('\n');

    setForm((f) => ({
      ...f,
      sujet,
      message: lines,
    }));
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) { toast.error('Vous devez accepter la politique RGPD.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/contact', form);
      setSuccess(res.data);
      toast.success('Message envoyé');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(parseApiError(err, "Erreur lors de l'envoi."), { duration: 6000 });
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="section-pad">
        <div className="container-app max-w-2xl">
          <div className="card-base p-8 text-center" data-testid="contact-success">
            <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="h2 mt-4 text-anthracite-950">Message envoyé</h1>
            <p className="lead mt-2">{success.message}</p>
            <p className="mt-4 text-sm text-anthracite-700">Référence : <span className="font-mono font-semibold">{success.reference}</span></p>
            <Button className="mt-6" onClick={() => { setSuccess(null); setForm({ nom: '', email: '', telephone: '', sujet: '', message: '', rgpd: false }); }}>Envoyer un autre message</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="hero-wash">
        <div className="container-app pt-14 pb-10">
          <Reveal>
            <p className="badge-soft mb-3">Contact</p>
            <h1 className="h1 text-anthracite-950">Nous contacter</h1>
            <p className="lead mt-3 max-w-3xl">Pour toute question, demande d'information ou prise de rendez-vous, notre équipe est à votre écoute.</p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-app grid lg:grid-cols-2 gap-8">
          {/* Info */}
          <Reveal>
            <div className="space-y-4">
              <InfoCard icon={Phone} title="Téléphone" lines={[
                { label: 'Garnat-sur-Engièvre', value: '04 70 42 72 63', href: 'tel:0470427263' },
                { label: 'Diou', value: '04 70 42 90 08', href: 'tel:0470429008' },
                { label: 'Tronget', value: '04 70 47 10 30', href: 'tel:0470471030' },
                { label: 'Avermes / Saint-Ennemond', value: '06 56 66 05 71', href: 'tel:0656660571' },
              ]} />
              <InfoCard icon={Mail} title="Email" lines={[
                { value: MAIN_EMAIL, href: `mailto:${MAIN_EMAIL}` }
              ]} />
              <InfoCard icon={MapPin} title="Zone d'intervention" lines={[
                { value: "Département de l'Allier (03)" },
                { value: 'Garnat-sur-Engièvre • Diou • Tronget • Avermes Saint-Ennemond • Noyant-d’Allier • Cressanges' }
              ]} />
              <InfoCard icon={Clock} title="Disponibilité" lines={[
                { value: 'Selon planning - Joignable toute l’année' },
                { value: 'Pour toute urgence vitale : composez le 15 (SAMU)' }
              ]} />
              <a
                href="https://www.google.com/maps?q=Avermes,Allier,France"
                target="_blank"
                rel="noopener noreferrer"
                className="card-base p-5 flex items-center gap-3 hover:border-brand-300"
                data-testid="contact-google-maps"
              >
                <div className="h-11 w-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className="font-heading font-semibold text-anthracite-950">Voir sur Google Maps</p>
                  <p className="text-sm text-muted-foreground">Ouvrir l'itinéraire vers l'Allier</p>
                </div>
                <ExternalLink className="h-4 w-4 text-muted-foreground" />
              </a>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal delay={100}>
            <form onSubmit={submit} className="card-base p-6 space-y-4" data-testid="contact-form">
              <h2 className="h3 text-anthracite-950">Écrivez-nous</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Nom complet *</Label><Input required value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} className="mt-1.5" data-testid="contact-nom" /></div>
                <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1.5" data-testid="contact-email" /></div>
              </div>
              <div><Label>Téléphone</Label><Input type="tel" value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} className="mt-1.5" data-testid="contact-telephone" /></div>
              <div><Label>Sujet *</Label><Input required value={form.sujet} onChange={e => setForm(f => ({ ...f, sujet: e.target.value }))} className="mt-1.5" data-testid="contact-sujet" /></div>
              <div><Label>Message *</Label><Textarea required value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} className="mt-1.5 min-h-[140px]" data-testid="contact-message" /></div>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={form.rgpd} onCheckedChange={(c) => setForm(f => ({ ...f, rgpd: !!c }))} data-testid="contact-rgpd" />
                <span className="text-sm text-anthracite-800">J'accepte que mes données soient utilisées pour répondre à mon message.</span>
              </label>
              <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="contact-submit-button">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Envoi...</> : 'Envoyer le message'}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, lines }) {
  return (
    <div className="card-base p-5">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-anthracite-950 mb-2">{title}</h3>
          <ul className="space-y-1.5">
            {lines.map((l, i) => (
              <li key={i} className="text-sm text-anthracite-700">
                {l.label && <span className="text-xs text-muted-foreground mr-2">{l.label} :</span>}
                {l.href ? <a href={l.href} className="font-medium text-brand-700 hover:text-brand-800">{l.value}</a> : l.value}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
